import { useContext, useMemo } from 'react'
import { com } from '@eidu/form'
import { EntityTypesContext } from '../../io/context/EntityTypes'
import useEntitiesOfType from '../../io/useEntitiesOfType'
import { ADMIN_SCHEMA_NAME, formatTypeName, getTypeDisplayName } from '../../util/entityType/typeNameSchema'
import { AssetTypeDefinition, findAssetTypeByLabel } from './assetTypes'
import { fieldValueToString } from '../../domain/entity/DraftFieldValue'
import EqualityHashMap from '../../util/EqualityHashMap'
import EntityWithLabelAndRelated from '../../domain/entity/EntityWithLabelAndRelated'
import EntityType = com.eidu.sharedlib.entity.type.EntityType
import EntityTypeId = com.eidu.sharedlib.entity.type.EntityTypeId
import FieldId = com.eidu.sharedlib.entity.field.FieldId

export const ADMIN_ASSET_TYPES_NAME = formatTypeName(ADMIN_SCHEMA_NAME, 'Asset Types')

export type AssetEntityTypeConfig = {
  entityType: EntityType
  entityTypeId: EntityTypeId
  entityTypeName: string
  allowedTypes: AssetTypeDefinition[]
  allowedTypeIds: string[]
  isMultiType: boolean
  payloadFieldId: FieldId
  typeFieldId?: FieldId
  thumbnailFieldId?: FieldId
}

export type AssetTypeRegistry = {
  assetEntityTypes: AssetEntityTypeConfig[]
  assetEntityTypesById: ReadonlyMap<EntityTypeId, AssetEntityTypeConfig>
  loading: boolean
  source: 'admin' | 'fallback'
}

const normalize = (value: string) => value.trim().toLowerCase()

const findEntityTypeByName = (
  entityTypes: ReadonlyMap<EntityTypeId, EntityType>,
  targetName: string
): EntityType | undefined => {
  const normalizedTarget = normalize(targetName)
  const types = Array.from(entityTypes.values())
  const exactMatch = types.find((type) => normalize(type.name) === normalizedTarget)
  if (exactMatch) return exactMatch
  return types.find((type) => normalize(getTypeDisplayName(type.name)) === normalizedTarget)
}

const findFieldByName = (entityType: EntityType, targetName: string) =>
  entityType.fields.asJsReadonlyArrayView().find((field) => normalize(field.name) === normalize(targetName))

const parseTypeList = (value: string) =>
  value
    .split(/\s+/)
    .map((entry) => entry.trim())
    .filter((entry) => entry.length > 0)

const buildEntityTypeConfig = (
  entityType: EntityType,
  allowedTypes: AssetTypeDefinition[]
): AssetEntityTypeConfig | undefined => {
  const uniqueTypes = Array.from(new Map(allowedTypes.map((type) => [type.id, type])).values())
  if (uniqueTypes.length === 0) return undefined

  const payloadField = findFieldByName(entityType, 'Payload')
  if (!payloadField) return undefined

  const typeField = findFieldByName(entityType, 'Type')
  const thumbnailField = findFieldByName(entityType, 'Thumbnail')
  const isMultiType = uniqueTypes.length > 1
  if (isMultiType && !typeField) return undefined

  return {
    entityType,
    entityTypeId: entityType.id,
    entityTypeName: getTypeDisplayName(entityType.name),
    allowedTypes: uniqueTypes,
    allowedTypeIds: uniqueTypes.map((type) => type.id),
    isMultiType,
    payloadFieldId: payloadField.id,
    typeFieldId: typeField?.id,
    thumbnailFieldId: thumbnailField?.id,
  }
}

const buildConfigsFromAdmin = (
  entityTypes: ReadonlyMap<EntityTypeId, EntityType>,
  adminType: EntityType,
  entries: ReadonlyMap<unknown, EntityWithLabelAndRelated>
): AssetEntityTypeConfig[] => {
  const nameField = findFieldByName(adminType, 'Name')
  const typesField = findFieldByName(adminType, 'Types')
  if (!nameField || !typesField) return []

  const merged = new Map<string, { entityType: EntityType; allowedTypes: Map<string, AssetTypeDefinition> }>()

  entries.forEach((entry) => {
    const values = entry.entity.valuesByFieldId.asJsReadonlyMapView()
    const entityTypeName = fieldValueToString(values.get(nameField.id) ?? null).trim()
    if (!entityTypeName) return

    const targetEntityType = findEntityTypeByName(entityTypes, entityTypeName)
    if (!targetEntityType) return

    const typesValue = fieldValueToString(values.get(typesField.id) ?? null).trim()
    const typeLabels = parseTypeList(typesValue)
    if (typeLabels.length === 0) return

    const resolvedTypes = typeLabels
      .map((label) => findAssetTypeByLabel(label))
      .filter((type): type is AssetTypeDefinition => !!type)

    if (resolvedTypes.length === 0) return

    const key = targetEntityType.id.toString()
    const entryState =
      merged.get(key) ??
      (() => {
        const next = { entityType: targetEntityType, allowedTypes: new Map<string, AssetTypeDefinition>() }
        merged.set(key, next)
        return next
      })()

    resolvedTypes.forEach((type) => entryState.allowedTypes.set(type.id, type))
  })

  return Array.from(merged.values())
    .map((entry) => buildEntityTypeConfig(entry.entityType, Array.from(entry.allowedTypes.values())))
    .filter((config): config is AssetEntityTypeConfig => !!config)
}

export const useAssetTypeRegistry = (
  overrideEntityTypes?: ReadonlyMap<EntityTypeId, EntityType>
): AssetTypeRegistry => {
  const entityTypesRepo = useContext(EntityTypesContext)
  const repoEntityTypes = entityTypesRepo.useAll()
  const entityTypes = overrideEntityTypes ?? repoEntityTypes

  const assetTypesAdminType = useMemo(
    () => (entityTypes ? findEntityTypeByName(entityTypes, ADMIN_ASSET_TYPES_NAME) : undefined),
    [entityTypes]
  )

  const adminEntriesByType = useEntitiesOfType(
    {
      typeIds: assetTypesAdminType ? [assetTypesAdminType.id] : [],
      allTypes: entityTypes ?? new Map(),
      pageSize: 200,
    },
    [assetTypesAdminType?.id.toString() ?? '', entityTypes?.size ?? 0]
  )

  const adminEntries = assetTypesAdminType ? adminEntriesByType?.get(assetTypesAdminType.id) : undefined
  const loading = assetTypesAdminType ? !adminEntriesByType : false

  const { assetEntityTypes, source } = useMemo(() => {
    if (!entityTypes) {
      return { assetEntityTypes: [] as AssetEntityTypeConfig[], source: 'fallback' as const }
    }

    if (assetTypesAdminType) {
      if (!adminEntries) {
        return { assetEntityTypes: [] as AssetEntityTypeConfig[], source: 'admin' as const }
      }

      return {
        assetEntityTypes: buildConfigsFromAdmin(entityTypes, assetTypesAdminType, adminEntries),
        source: 'admin' as const,
      }
    }

    return { assetEntityTypes: [] as AssetEntityTypeConfig[], source: 'fallback' as const }
  }, [adminEntries, assetTypesAdminType, entityTypes])

  const assetEntityTypesById = useMemo(
    () =>
      new EqualityHashMap(
        assetEntityTypes.map((config) => [config.entityTypeId, config] as [EntityTypeId, AssetEntityTypeConfig])
      ),
    [assetEntityTypes]
  )

  return { assetEntityTypes, assetEntityTypesById, loading, source }
}
